import { useState, useEffect } from 'react';
import {
    isEmpty,
    cloneDeep,
} from 'lodash';

import {
    handleImageSelect,
} from './logoWizardUtils';

import { DEFAULT_IMAGE } from '../../../../commons/imageUploader/AdvertisingDefaultImage';
import useImageUpload from '../../../hooks/useImageUploadDynamicStore';

import DefaultRoundLogo from '../../../../components/ImageCrop/roundLogo';
import DefaultRectLogo from '../../../../components/ImageCrop/rectLogo';

import {
    prepareStateForImage,
    updateEditorStateLimits,
} from '../../../../components/ImageEditor/engineStateHelper';

import { AssetType } from '../../../../utils/constants';
import { useButtonsStore } from '../../../../store/models';
import { dataURIToFile } from '../../../../utils/image';

function useImageCrop (props) {
    const {
        styleData,
        setStyleData,
        type,
        wizardOpen,
        onClose,
        imageBrowsed = false,
    } = props;

    const buttonsStore = useButtonsStore();
    const [image, setImage] = useState(DEFAULT_IMAGE);

    const [bannerImage, setBannerImage] = useState(DEFAULT_IMAGE);
    const [defaultImage, setDefaultImage] = useState(DEFAULT_IMAGE);
    const [capturedImage, setCapturedImage] = useState(null);
    const [transform, updateTransform] = useState({ x: 0, y: 0, scale: 1 });
    const [uploading, setUploading] = useState(false);
    const [isImageBrowsed, setIsImageBrowsed] = useState(imageBrowsed);
    const [shadow, _setShadow] = useState(true);
    const [shape, _setShape] = useState('round');
    const [transparent, _setTransparent] = useState(true);

    async function setShape (v) {
        updateEditorStateLimits(v);
        if (!styleData.logo && !isImageBrowsed) {
            if (v === 'round') {
                await prepareStateForImage(DefaultRoundLogo, v);
            } else if (v === 'rect') {
                await prepareStateForImage(DefaultRectLogo, v);
            }
        } else {
            await prepareStateForImage(image, v);
        }

        _setShape(v);
        buttonsStore.stateChangeTarget = 'logoImageShapeDefault';
        pushStackNow({ logoShape: v });
        setStyleData({
            ...styleData,
            logoShape: v,
        });
    }

    function pushStackNow (newProps) {
        buttonsStore.stylesData = {
            ...buttonsStore.stylesData,
            ...newProps,
        };
        buttonsStore.pushLogoEditorActiveStates();
    }

    function setShadow (v) {
        _setShadow(v);
        buttonsStore.stateChangeTarget = 'logoShadow';
        pushStackNow({ logoShadow: v });
        setStyleData({
            ...styleData,
            logoShadow: v,
        });
    }

    function setTransparent (v) {
        _setTransparent(v);
        buttonsStore.stateChangeTarget = 'logoTransparent';
        pushStackNow({ logoTransparent: v });
        setStyleData({
            ...styleData,
            logoTransparent: v,
        });
    }

    function setImageURL (url) {
        setUploading(false);
        if (!wizardOpen) {
            return;
        }
        setStyleData({
            ...styleData,
            logo: url,
        });
        if (uploading) {
            onClose();
        }
    };

    function setBannerURL (url) {
        console.log('setBannerURL():', url);
        if (!wizardOpen) {
            return;
        }

        setStyleData({
            ...styleData,
            bannerURL: url,
        });

        if (uploading) {
            onClose();
        }
    }

    function getUploadProps () {
        if (type === 'banner') {
            return {
                assetType: AssetType.BUTTON_BANNER,
                setStateFunc: setBannerURL,
                shouldCheckFilesize: false,
                observableDataSource: buttonsStore.observed.assets.buttonBanner,
                consumableDataSource: buttonsStore.assets.buttonBanner,
            };
        }
        return {
            assetType: AssetType.BUTTON_LOGO,
            setStateFunc: setImageURL,
            shouldCheckFilesize: true,
            observableDataSource: buttonsStore.observed.assets.buttonLogo,
            consumableDataSource: buttonsStore.assets.buttonLogo,
        };
    }

    const {
        image: buttonLogo,
        upload: uploadButtonLogo,
        imageError: buttonLogoError,
        // deleteImage: deleteButtonLogo,
    } = useImageUpload({
        store: buttonsStore,
        uploadMethod: 'uploadWidgetAssets',
        customDefaultImage: defaultImage,
        ...getUploadProps(),
    });

    useEffect(() => {
        if (styleData.logoShape) {
            _setShape(styleData.logoShape);
        }

        if (styleData.version === '1' || isEmpty(styleData)) {
            _setShadow(false);
        }

        if (styleData.bannerURL) {
            setBannerImage(styleData.bannerURL);
        }

        return () => {
            buttonsStore.setIsImageBrowsed(false);
        };
    }, []);

    useEffect(() => {
        applyStyleData();
    }, [styleData.logoShape]);

    useEffect(() => {
       applyStyleData();
    }, [styleData]);

    useEffect(() => {
        console.log('File Size error:', buttonLogoError);
    }, [buttonLogoError]);

    useEffect(() => {
        updateImageByDefaults();
    }, [shape]);

    function applyStyleData () {
        if (styleData.version === '1' || isEmpty(styleData)) {
            _setShadow(false);
        } else {
            _setShadow(styleData.logoShadow);
        }

        if (styleData.logoTransparent === true || styleData.logoTransparent === undefined || styleData.logoTransparent === null) {
            _setTransparent(styleData.logoTransparent);
        } else {
            _setTransparent(false);
        }

        if (styleData.logoShape) {
            _setShape(styleData.logoShape);
        }
    }

    async function updateImageByDefaults () {
        if (!isImageBrowsed && type !== 'banner') {
            if (styleData.logo) {
                buttonsStore.setLogoImageURI(styleData.logo);
                buttonsStore.setImageAsDefault(false);
                setImage(styleData.logo);
                buttonsStore.validatePushOnUndoRedoEvents();
                return;
            }
            if (shape === 'round') {
                setDefaultImage(DefaultRoundLogo);
                if (!styleData.logo) {
                    buttonsStore.setLogoImageURI(DefaultRoundLogo);
                    buttonsStore.setImageAsDefault(true);
                    setImage(DefaultRoundLogo);
                    await prepareStateForImage(DefaultRoundLogo, shape);
                    buttonsStore.validatePushOnUndoRedoEvents();
                }
            } else if (shape === 'rect') {
                setDefaultImage(DefaultRectLogo);
                if (!styleData.logo) {
                    buttonsStore.setLogoImageURI(DefaultRectLogo);
                    buttonsStore.setImageAsDefault(true);
                    setImage(DefaultRectLogo);
                    await prepareStateForImage(DefaultRectLogo, shape);
                    buttonsStore.validatePushOnUndoRedoEvents();
                }
            }
        }
    }

    /*
     * For undo reset
     * Instead of waiting for styleData to be updated
     * image should be updated immediately at the same pass when stylesData is updated to reduce the delays
     */
    useEffect(() => {
        if (isEmpty(styleData.logo)) {
            resetLogoImageState();
        } else if (!isEmpty(styleData.logo)) {
            setImage(buttonsStore.logoImageURI);
        }
    }, [buttonsStore.observed.logoImageURI]);

    function resetLogoImageState () {
        // note do not push undo stack here this will only be called to update the image state
        setIsImageBrowsed(buttonsStore.isImageBrowsed);
        setImage(buttonsStore.logoImageURI);
    }

    function setTransform (p) {
        if (p) {
            updateTransform(p);
        } else {
            updateTransform({
                x: 0,
                y: 0,
                scale: 1,
            });
        }
    }

    function selectImage () {
        const btn = document.querySelector('#logoWizardSelect');
        btn.click();
    }

    async function imageSelection (e) {
        setIsImageBrowsed(true);
        const imageURI = await handleImageSelect(e.target);
        if (type === 'banner') {
            setBannerImage(imageURI);
        } else {
            buttonsStore.setLogoImageURI(imageURI);
            buttonsStore.setIsImageBrowsed(true);
            buttonsStore.setImageAsDefault(false);
            await prepareStateForImage(imageURI, shape);
            window.currentEditorState = cloneDeep(window.editorState);

            buttonsStore.pushLogoEditorActiveStates(); // wait for the image to take effect
            setImage(imageURI);
        }
    }

    function onChangeComplete (e) {
        if (e.transform) {
            setTransform(e.transform);
        }
        setCapturedImage(e.imageURI);
    }

    async function uploadCanvasDataURI () {
        setUploading(true);
        setStyleData({
            ...styleData,
            logoShape: shape,
        });
        const file = await dataURIToFile(capturedImage, 'button-logo.png');
        uploadButtonLogo([{
            file: file,
            data_url: capturedImage,
        }]);
    }

    return {
        buttonsStore,
        buttonLogo,
        image,
        selectImage,
        imageSelection,
        capturedImage,
        shape,
        setShape,
        onChangeComplete,
        transform,
        uploadCanvasDataURI,
        uploading,
        shadow,
        setShadow,
        bannerImage,
        transparent,
        setTransparent,
        initUndoRedo: () => {
            buttonsStore.initUndoRedo();
        },
        isImageBrowsed,
    };
}

export default useImageCrop;
